import React, { useState } from 'react'
import { toast } from 'sonner'
import { Eye, XCircle, Clock, Check, X, Calendar, AlertCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { leaveService } from '@/services/leaveService'

const LeaveHistoryTable = ({ leaves, onUpdate }) => {
  const [loading, setLoading] = useState(false)
  const [selectedLeave, setSelectedLeave] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [cancelLeaveId, setCancelLeaveId] = useState(null)

  const handleCancelLeave = async () => {
    if (!cancelLeaveId) return
    setLoading(true)
    try {
      const response = await leaveService.cancelLeave(cancelLeaveId)
      if (response.success) {
        toast.success('Leave request cancelled!')
        onUpdate?.()
      }
    } catch (error) {
      toast.error('Failed to cancel leave request')
    } finally {
      setLoading(false)
      setCancelLeaveId(null)
    }
  }

  const handleViewDetails = (leave) => {
    setSelectedLeave(leave)
    setIsModalVisible(true)
  }

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', className: 'border-yellow-500 text-yellow-600 bg-yellow-50', icon: Clock },
      approved: { variant: 'outline', className: 'border-emerald-500 text-emerald-600 bg-emerald-50', icon: Check },
      rejected: { variant: 'outline', className: 'border-red-500 text-red-600 bg-red-50', icon: X },
      cancelled: { variant: 'outline', className: 'border-gray-400 text-gray-600 bg-gray-50', icon: AlertCircle },
    }
    const { variant, className, icon: Icon } = config[status] || config.pending
    return (
      <Badge variant={variant} className={className}>
        <Icon className="mr-1 h-3 w-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    )
  }

  const getLeaveTypeInfo = (type) => {
    const typeLabels = {
      sick: { label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
      vacation: { label: 'Vacation', color: 'bg-blue-100 text-blue-700' },
      personal: { label: 'Personal', color: 'bg-purple-100 text-purple-700' },
      maternity: { label: 'Maternity', color: 'bg-pink-100 text-pink-700' },
      paternity: { label: 'Paternity', color: 'bg-indigo-100 text-indigo-700' },
      emergency: { label: 'Emergency', color: 'bg-orange-100 text-orange-700' },
      other: { label: 'Other', color: 'bg-gray-100 text-gray-700' }
    }
    return typeLabels[type] || { label: type, color: 'bg-gray-100 text-gray-700' }
  }

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = end - start
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  if (leaves.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <EmptyState
            icon="calendar"
            title="No leave requests"
            description="You haven't submitted any leave requests yet."
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Leave History</CardTitle>
          <CardDescription>Your submitted leave requests and their status</CardDescription>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-semibold">Leave Type</TableHead>
              <TableHead className="font-semibold">Duration</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Requested</TableHead>
              <TableHead className="font-semibold">Reviewed</TableHead>
              <TableHead className="text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaves.map((leave) => {
              const typeInfo = getLeaveTypeInfo(leave.leave_type)
              const days = calculateDays(leave.start_date, leave.end_date)
              return (
                <TableRow key={leave.id} className="group">
                  <TableCell>
                    <Badge variant="secondary" className={typeInfo.color}>
                      {typeInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 font-medium text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {days} {days === 1 ? 'day' : 'days'}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(leave.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(leave.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(leave.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(leave.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {leave.reviewed_at 
                      ? new Date(leave.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : <span className="text-muted-foreground/60">-</span>
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(leave)}
                        className="h-8 px-2"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {leave.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setCancelLeaveId(leave.id)}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Leave Details Modal */}
      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Leave Request Details</DialogTitle>
            <DialogDescription>View the complete details of your leave request</DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-6">
              {/* Leave Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Leave Type</p>
                  <Badge variant="secondary" className={getLeaveTypeInfo(selectedLeave.leave_type).color}>
                    {getLeaveTypeInfo(selectedLeave.leave_type).label}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                  {getStatusBadge(selectedLeave.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start Date</p>
                  <p className="font-medium">{new Date(selectedLeave.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">End Date</p>
                  <p className="font-medium">{new Date(selectedLeave.end_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
              </div>

              <div className="p-3 bg-primary/5 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Total Duration</p>
                <p className="text-xl font-semibold text-primary">
                  {calculateDays(selectedLeave.start_date, selectedLeave.end_date)} {calculateDays(selectedLeave.start_date, selectedLeave.end_date) === 1 ? 'day' : 'days'}
                </p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Requested</p>
                  <p className="text-sm">{new Date(selectedLeave.requested_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reviewed</p>
                  <p className="text-sm">{selectedLeave.reviewed_at ? new Date(selectedLeave.reviewed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Pending review'}</p>
                </div>
              </div>

              {selectedLeave.reason && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reason</p>
                  <p className="text-sm p-3 bg-muted/50 rounded-lg">{selectedLeave.reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelLeaveId} onOpenChange={() => setCancelLeaveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Leave Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this leave request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelLeave} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancel Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default LeaveHistoryTable
